import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { quizAPI } from '../services/api';
import { FiArrowLeft, FiClock, FiCheckCircle, FiXCircle, FiTrendingUp } from 'react-icons/fi';

const QuizHistory = () => {
  const { user } = useAuth();
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedQuiz, setSelectedQuiz] = useState(null);

  useEffect(() => {
    fetchHistory();
  }, []);

  const fetchHistory = async () => {
    try {
      const response = await quizAPI.getQuizHistory(user.id);
      if (response.data.success) {
        setHistory(response.data.history);
      }
    } catch (error) {
      console.error('Error fetching history:', error);
    } finally {
      setLoading(false);
    }
  };

  const getGradeColor = (percentage) => {
    if (percentage >= 90) return 'text-green-600 bg-green-50';
    if (percentage >= 70) return 'text-blue-600 bg-blue-50';
    if (percentage >= 50) return 'text-yellow-600 bg-yellow-50';
    return 'text-red-600 bg-red-50';
  };

  const getGrade = (percentage) => {
    if (percentage >= 90) return 'A+';
    if (percentage >= 80) return 'A';
    if (percentage >= 70) return 'B';
    if (percentage >= 60) return 'C';
    if (percentage >= 50) return 'D';
    return 'F';
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center">
              <Link to="/dashboard" className="mr-4 p-2 rounded-lg hover:bg-gray-100">
                <FiArrowLeft className="w-5 h-5" />
              </Link>
              <h1 className="text-2xl font-bold text-gray-800">Quiz History</h1>
            </div>
            <div className="flex items-center space-x-2 text-primary-500">
              <FiTrendingUp className="w-5 h-5" />
              <span className="font-semibold">{history.length} Quizzes</span>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {loading ? (
          <div className="text-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-b-4 border-primary-500 mx-auto"></div>
            <p className="text-gray-600 mt-4">Loading history...</p>
          </div>
        ) : history.length === 0 ? (
          <div className="card text-center py-12">
            <div className="w-24 h-24 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <FiClock className="w-12 h-12 text-gray-400" />
            </div>
            <h3 className="text-xl font-bold text-gray-800 mb-2">No Quiz History</h3>
            <p className="text-gray-600 mb-6">You haven't taken any quizzes yet</p>
            <Link to="/dashboard" className="btn-primary inline-block">
              Take Your First Quiz
            </Link>
          </div>
        ) : (
          <div className="grid grid-cols-1 gap-6">
            {history.map((quiz, index) => {
              const quizData = quiz.quiz_data ? JSON.parse(quiz.quiz_data) : null;
              const isExam = quiz.type === 'exam';
              
              return (
                <div key={index} className="card hover:shadow-xl transition-all">
                  {/* Type Badge */}
                  <div className="flex items-center justify-between mb-3">
                    <span className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-bold ${
                      isExam 
                        ? 'bg-gradient-to-r from-purple-100 to-pink-100 text-purple-700 border border-purple-300' 
                        : 'bg-gradient-to-r from-blue-100 to-indigo-100 text-blue-700 border border-blue-300'
                    }`}>
                      {isExam ? '📝 Test Series' : '🎯 Practice Quiz'}
                    </span>
                    {isExam && quiz.exam_name && (
                      <span className="text-sm font-semibold text-gray-700">{quiz.exam_name}</span>
                    )}
                  </div>

                  <div className="flex flex-col md:flex-row md:items-center md:justify-between">
                    {/* Quiz Info */}
                    <div className="flex-1 mb-4 md:mb-0">
                      <div className="flex items-center space-x-3 mb-2">
                        <FiClock className="text-gray-400" />
                        <span className="text-sm text-gray-500">
                          {new Date(quiz.created_at).toLocaleDateString('en-US', {
                            year: 'numeric',
                            month: 'long',
                            day: 'numeric',
                            hour: '2-digit',
                            minute: '2-digit'
                          })}
                        </span>
                      </div>
                      
                      <div className="flex items-center space-x-4">
                        <div>
                          <p className="text-gray-600 text-sm">Questions</p>
                          <p className="text-2xl font-bold text-gray-800">{quiz.total_questions}</p>
                        </div>
                        <div>
                          <p className="text-gray-600 text-sm">Correct</p>
                          <p className="text-2xl font-bold text-green-600">{quiz.correct_answers}</p>
                        </div>
                        <div>
                          <p className="text-gray-600 text-sm">Wrong</p>
                          <p className="text-2xl font-bold text-red-600">
                            {quiz.total_questions - quiz.correct_answers}
                          </p>
                        </div>
                      </div>
                      
                      {/* Extra info for exams */}
                      {isExam && quizData && (
                        <div className="mt-3 pt-3 border-t border-gray-200">
                          <div className="flex items-center space-x-4 text-xs">
                            <span className="text-green-600 font-semibold">
                              +{quizData.total_marks} marks
                            </span>
                            <span className="text-red-600 font-semibold">
                              -{quizData.negative_marks} marks
                            </span>
                            <span className="text-indigo-600 font-bold">
                              Final: {quizData.actual_score}/{quizData.max_score}
                            </span>
                          </div>
                        </div>
                      )}
                    </div>

                    {/* Score Display */}
                    <div className="flex items-center space-x-4">
                      <div className={`px-6 py-4 rounded-lg ${getGradeColor(quiz.percentage)}`}>
                        <p className="text-sm font-medium">Grade</p>
                        <p className="text-4xl font-bold">{getGrade(quiz.percentage)}</p>
                      </div>
                      <div className="text-center">
                        <p className="text-sm text-gray-600 mb-1">Score</p>
                        <div className={`text-3xl font-bold ${
                          quiz.percentage >= 70 ? 'text-green-500' :
                          quiz.percentage >= 50 ? 'text-yellow-500' :
                          'text-red-500'
                        }`}>
                          {quiz.percentage}%
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* View Details Button */}
                  {quizData && (
                    <div className="mt-4 pt-4 border-t">
                      <button
                        onClick={() => setSelectedQuiz(selectedQuiz === index ? null : index)}
                        className="text-primary-500 hover:text-primary-600 font-medium text-sm"
                      >
                        {selectedQuiz === index ? 'Hide Details' : 'View Details'} →
                      </button>

                      {/* Detailed Results */}
                      {selectedQuiz === index && quizData.results && (
                        <div className="mt-4 space-y-3">
                          {quizData.results.map((result, idx) => (
                            <div
                              key={idx}
                              className={`p-4 rounded-lg border-2 ${
                                result.is_correct
                                  ? 'border-green-200 bg-green-50'
                                  : 'border-red-200 bg-red-50'
                              }`}
                            >
                              <div className="flex items-start justify-between mb-2">
                                <h4 className="font-semibold text-gray-800 flex-1">
                                  Q{idx + 1}. {result.question}
                                </h4>
                                {result.is_correct ? (
                                  <FiCheckCircle className="text-green-500 w-6 h-6 flex-shrink-0 ml-2" />
                                ) : (
                                  <FiXCircle className="text-red-500 w-6 h-6 flex-shrink-0 ml-2" />
                                )}
                              </div>
                              
                              <div className="text-sm space-y-1">
                                <p>
                                  <span className="font-medium">Your Answer:</span>{' '}
                                  <span className={result.is_correct ? 'text-green-600' : 'text-red-600'}>
                                    {result.selected}
                                  </span>
                                </p>
                                {!result.is_correct && (
                                  <p>
                                    <span className="font-medium">Correct Answer:</span>{' '}
                                    <span className="text-green-600">{result.correct}</span>
                                  </p>
                                )}
                                {result.explanation && (
                                  <div className="mt-2 pt-2 border-t border-gray-200">
                                    <p className="font-medium text-gray-700">Explanation:</p>
                                    <p className="text-gray-600">{result.explanation}</p>
                                  </div>
                                )}
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </main>
    </div>
  );
};

export default QuizHistory;

